import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  useTheme,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import usePlanStore from 'store/planStore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'pages/calculator/MediaPlanning/Plan/PlanSummary/styles';
import { AccordionSummaryContent } from 'pages/calculator/MediaPlanning/Plan/PlanSummary/AccordionSummaryContent/AccordionSummaryContent';
import DigitalBudgetTable from 'pages/calculator/MediaPlanning/common/BudgetTable/DigitalBudgetTable/DigitalBudgetTable';
import OfflineBudgetTable from 'pages/calculator/MediaPlanning/common/BudgetTable/OfflineBudgetTable/OfflineBudgetTable';
import { TotalBudgetSummary } from 'pages/calculator/MediaPlanning/Plan/PlanSummary/TotalBudgetSummary/TotalBudgetSummary';
import { groupCalculatedBudgetTouchpointData } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/groupTouchpointData';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Countries } from 'shared/enums';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import useGenerateXls from './hooks/useGenerateXls';
import useSavePlan from './hooks/useSavePlan';
import useUpdatePlan from './hooks/useUpdatePlan';
import SavedPlanSnackbar from './SavedPlanSnackbar/SavedPlanSnackbar';
import getMissingMandatoryTouchpoints from './helpers/getMissingMandatoryTouchpoints';
import { useAuthContext } from '../../../../../context/AuthContext';
import BookingFlow from './BookingFlow/BookingFlow';
import SaveBookingFlow from './SaveBookingFlow/SaveBookingFlow';
import useMetaPredictionStatuses from './OverridingBookingFlow/hooks/useMetaPredictionStatuses';
import useBookMetaStart from './hooks/useBookMetaStart';
import DDXRadios from './DDXRadios/DDXRadios';
import { formatNumberWithSpaces } from '../../common/BudgetTable/utils/formatNumberWithSpaces';
import { getBudgetTotals, isDdxPickedForAllCountries } from './helpers/helpers';
import useDdxSetup from '../GeneralInfo/DivisionSelect/hooks/useDDXSetup';
import getAbilityForReservation from './SaveBookingFlow/helpers/getAbilityForReservation';
import AcceptPlanFlow from './AcceptPlanFlow/AcceptPlanFlow';
import useAcceptUnlockMediaPlan from './hooks/useAcceptUnlockMediaPlan';
import statuses from '../../MediaPlansTable/helpers/statuses';
import PlanSummaryGeneralInfo from './PlanSummaryGeneralInfo/PlanSummaryGeneralInfo';
import getAbilityForAccepting from './helpers/getAbilityForAccepting';

function countryNameToISO(countryName: string): string | null {
  return (
    (Countries as unknown as Record<string, string>)[
      countryName.toUpperCase()
    ] ?? null
  );
}

function isNullsSentinel(ddx: any) {
  return (
    ddx != null && ddx.type == null && ddx.value == null && ddx.totalDDX == null
  );
}

function isDdxNotApplicableSetup(setup: any) {
  if (!setup) return true;
  return (
    setup.isMustHave == null &&
    setup.isPercentage == null &&
    setup.isFixed == null
  );
}

function PlanSummary() {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [saveWithBookMetaOpen, setSaveWithBookMetaOpen] = useState(false);
  const [acceptPlanOpen, setAcceptPlanOpen] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [bookMetaOpen, setBookMetaOpen] = useState(false);
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { generateXls } = useGenerateXls();
  const { userRoles } = useAuthContext();
  const isPlanAccepted = false;
  const navigate = useNavigate();

  const {
    countryForms,
    alreadySavedPlanId,
    storedCountryForms,
    ddxSetupByCountry,
    setCountryDdx,
    storeCountryFormState,
    isLocked,
  } = usePlanStore();

  const { id } = useParams();

  const { isLoading } = useDdxSetup();

  const {
    asyncSaveMediaPlan,
    isLoading: isSavingMediaPlan,
    data: savedPlanData,
  } = useSavePlan();

  const {
    asyncUpdateMediaPlan,
    isLoading: isUpdatingMediaPlan,
    data: updatedPlanData,
  } = useUpdatePlan(id);

  const {
    asyncAcceptUnlockMediaPlanTrigger,
    isLoading: isAcceptingMediaPlan,
    data: acceptedPlanData,
  } = useAcceptUnlockMediaPlan();

  const { getMetaPredictionStatuses } = useMetaPredictionStatuses();
  const {
    asyncBookMetaStartTrigger,
    isLoading: isBookingLoading,
    error: bookingError,
    data: pollingResults,
    isPolling,
  } = useBookMetaStart(getMetaPredictionStatuses);

  const hasAnyCalculatedBudget =
    Object.values(storedCountryForms).some(
      (form) => (form.calculatedBudget ?? []).length > 0
    ) ||
    Object.values(storedCountryForms).some(
      (form) => (form.offline?.tv?.offlineBudget ?? []).length > 0
    );

  const totals = useMemo(
    () => getBudgetTotals(storedCountryForms),
    [storedCountryForms]
  );

  useEffect(() => {
    if (isLoading) return;

    Object.keys(storedCountryForms).forEach((country) => {
      const iso = countryNameToISO(country);
      const setup = iso ? ddxSetupByCountry?.[iso] : null;

      const notApplicable = isDdxNotApplicableSetup(setup);
      const hasDigital = (
        storedCountryForms[country]?.calculatedBudget ?? []
      ).some((b: any) => b.isIncludedInCalculations);

      const hasOffline =
        (storedCountryForms[country]?.offline?.tv?.offlineBudget ?? []).length >
        0;

      if (!hasDigital && !hasOffline) return;

      if (!notApplicable) return;

      const ddx = storedCountryForms[country]?.ddx;

      if (!isNullsSentinel(ddx)) {
        setCountryDdx(country, {
          type: null,
          value: null,
          totalDDX: null,
        } as any);
        storeCountryFormState(country);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, ddxSetupByCountry, storedCountryForms]);

  useEffect(() => {
    if (savedPlanData || updatedPlanData) {
      setShowSnackbar(true);
    }
  }, [savedPlanData, updatedPlanData]);

  const handleCloseBookMeta = () => {
    setSaveWithBookMetaOpen(false);
    setBookMetaOpen(false);
  };

  const handleCloseAcceptPlan = () => {
    setAcceptPlanOpen(false);
  };

  const handleSavePlan = async () => {
    await asyncSaveMediaPlan();
  };

  const handleUpdatePlan = async (passedId: string | null) => {
    getMetaPredictionStatuses();
    await asyncUpdateMediaPlan(passedId);
  };

  const handlePlanAcceptation = (passedId: string | null) => {
    if (!passedId) return;

    setAcceptError(null);

    asyncAcceptUnlockMediaPlanTrigger(
      { id: passedId, action: 'accept' },
      {
        onSuccess: () => {
          setAcceptPlanOpen(false);
          navigate(`/media-planning/accepted/${passedId}`);
        },
        onError: (err: any) => {
          const message =
            err?.response?.data?.message ||
            err?.message ||
            t('common.somethingWentWrong');

          setAcceptError(message);
          setAcceptPlanOpen(true);
        },
      }
    );
  };

  const handleSavePlanOnly = () => {
    if (id || alreadySavedPlanId) {
      handleUpdatePlan(id ?? alreadySavedPlanId);
    } else {
      handleSavePlan();
    }
    setSaveWithBookMetaOpen(false);
  };

  const handleAcceptPlan = () => {
    if (id || alreadySavedPlanId) {
      handlePlanAcceptation(id ?? alreadySavedPlanId);
    } else {
      // add unlocking logic
    }
    setAcceptPlanOpen(false);
  };

  const isAnyMetaNotReserved = Object.values(storedCountryForms).some(
    (country) => country.metaConfig?.reservationStatus !== 'reserved'
  );

  const planHasMetaRF = Object.values(storedCountryForms).some((country) =>
    country.availableTouchpoints?.some(
      (touchpoint) =>
        touchpoint.touchpointFunnel === 'Reservation awareness (RF)'
    )
  );

  const handleClickAcceptPlan = () => {
    setAcceptError(null);
    setAcceptPlanOpen(true);
  };

  const handleClickSavePlan = () => {
    if (planHasMetaRF && isAnyMetaNotReserved) {
      setSaveWithBookMetaOpen(true);
    } else {
      handleSavePlanOnly();
    }
  };

  const handleClickBookMeta = async () => {
    if (!planHasMetaRF) return;

    if (id || alreadySavedPlanId) {
      await handleUpdatePlan(id ?? alreadySavedPlanId);
    } else {
      await asyncSaveMediaPlan();
    }

    const latestPlanId = usePlanStore.getState().alreadySavedPlanId;
    setSaveWithBookMetaOpen(false);
    setBookMetaOpen(true);

    await asyncBookMetaStartTrigger({ planId: id || latestPlanId });
  };

  const getButtonContent = () => {
    if (isSavingMediaPlan || isUpdatingMediaPlan) {
      return <CircularProgress size={16} sx={{ color: palette.border.main }} />;
    }
    return t('mediaPlanning.savePlan');
  };

  const missingMandatoryTouchpoints =
    getMissingMandatoryTouchpoints(storedCountryForms);

  if (!Object.keys(countryForms).length || !hasAnyCalculatedBudget) {
    return (
      <Box
        sx={{
          my: '17px',
          p: '24px',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '5px',
          backgroundColor: 'common.white',
        }}
      >
        <Typography>{t('mediaPlanning.noCalculatedBudget')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        my: '17px',
        pb: '40px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '5px',
        backgroundColor: 'common.white',
      }}
    >
      <Grid container sx={{ p: '24px 15px' }}>
        <Grid item xs={12}>
          <Typography
            variant="body2"
            textTransform="uppercase"
            fontWeight={700}
            color="lorealPurple.main"
          >
            {t('mediaPlanning.planSummary')}
          </Typography>
        </Grid>
      </Grid>
      <PlanSummaryGeneralInfo />
      <TotalBudgetSummary isDDXLoading={isLoading} />

      {Object.keys(storedCountryForms).map((country) => {
        const processedData = storedCountryForms[
          country
        ]?.offline?.tv?.offlineBudget?.map((budget: any) => {
          if (budget.touchpoint !== 'outdoor') return budget;
          return {
            ...budget,
            children:
              budget.children?.filter(
                (child: any) => child.isIncludedInCalculations
              ) || [],
          };
        });

        const processedDigitalData = storedCountryForms[
          country
        ]?.calculatedBudget?.filter(
          (budget: any) => budget.isIncludedInCalculations
        );

        const hasAny =
          (processedDigitalData?.length ?? 0) > 0 ||
          (storedCountryForms[country]?.offline?.tv?.offlineBudget?.length ??
            0) > 0;

        if (!hasAny) return null;

        return (
          <Accordion key={country}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ position: 'relative', zIndex: 2 }}
            >
              <AccordionSummaryContent
                country={country}
                missingMandatoryTouchpoints={missingMandatoryTouchpoints}
                isDDXLoading={isLoading}
              />
            </AccordionSummary>
            <AccordionDetails>
              <DigitalBudgetTable
                data={groupCalculatedBudgetTouchpointData(
                  processedDigitalData || []
                )}
                goldenRuleMap={{
                  META: {
                    grReach: storedCountryForms[country]?.metaConfig?.grReach,
                    isGoldenRuleMet:
                      storedCountryForms[country]?.metaConfig?.isGoldenRuleMet,
                    reach: storedCountryForms[country]?.metaConfig
                      ?.saturationMin
                      ? (storedCountryForms[country]?.metaConfig
                          ?.reachEstimateMin ?? 0) *
                        (storedCountryForms[country]?.metaConfig?.saturation ??
                          0)
                      : 0,
                  },
                  YOUTUBE: {
                    grReach:
                      storedCountryForms[country]?.youTubeConfig?.grReach,
                    isGoldenRuleMet:
                      storedCountryForms[country]?.youTubeConfig
                        ?.isGoldenRuleMet,
                    reach: storedCountryForms[country]?.youTubeConfig
                      ?.saturationMin
                      ? (storedCountryForms[country]?.youTubeConfig?.targetGroup
                          ?.targetSizePopulation ?? 0) *
                        (storedCountryForms[country]?.youTubeConfig
                          ?.saturation ?? 0)
                      : 0,
                  },
                  TIKTOK: {
                    grReach: storedCountryForms[country]?.tiktokConfig?.grReach,
                    isGoldenRuleMet:
                      storedCountryForms[country]?.tiktokConfig
                        ?.isGoldenRuleMet,
                    reach: storedCountryForms[country]?.tiktokConfig
                      ?.saturationMin
                      ? (storedCountryForms[country]?.tiktokConfig?.targetGroup
                          ?.targetSizePopulation ?? 0) *
                        (storedCountryForms[country]?.tiktokConfig
                          ?.saturation ?? 0)
                      : 0,
                  },
                }}
                goldenRuleAlign="right"
              />
              <OfflineBudgetTable data={processedData || []} />
              <DDXRadios
                countryName={country}
                baseBudget={totals.totalBudget}
                currency="PLN"
                format={formatNumberWithSpaces}
                isDDXLoading={isLoading}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Grid
        container
        sx={{
          px: '24px',
          pt: '40px',
          borderTop: '1px solid',
          borderColor: 'border.main',
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            columnGap: '24px',
          }}
        >
          {savedPlanData?.id ||
          updatedPlanData?.id ||
          id ||
          alreadySavedPlanId ? (
            <Button
              variant="text"
              sx={{
                borderRadius: '30px',
                height: '34px',
                position: 'relative',
                zIndex: 2,
              }}
              onClick={() => generateXls(id ?? alreadySavedPlanId ?? undefined)}
            >
              {t('mediaPlanning.downloadXls')}
            </Button>
          ) : null}

          {isAnyMetaNotReserved &&
            planHasMetaRF &&
            getAbilityForReservation(userRoles) && (
              <Button
                disabled={
                  !!Object.keys(missingMandatoryTouchpoints).length ||
                  !isDdxPickedForAllCountries(storedCountryForms)
                }
                onClick={handleClickBookMeta}
                variant="outlined"
                sx={{ borderRadius: '30px', height: '34px', minWidth: '153px' }}
              >
                {t('mediaPlanning.bookMeta')}
              </Button>
            )}
          {getAbilityForAccepting(userRoles) && (
            <Button
              disabled={
                isLocked ||
                !!Object.keys(missingMandatoryTouchpoints).length ||
                !isDdxPickedForAllCountries(storedCountryForms) ||
                (!alreadySavedPlanId && !id)
              }
              onClick={handleClickAcceptPlan}
              variant="outlined"
              sx={{ borderRadius: '30px', height: '34px', minWidth: '153px' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {isPlanAccepted ? <LockOpenIcon /> : <LockOutlinedIcon />}
                <span>
                  {isPlanAccepted
                    ? t('mediaPlanning.unlockPlan')
                    : t('mediaPlanning.acceptPlan')}
                </span>
              </Stack>
            </Button>
          )}
          <Button
            disabled={
              !!Object.keys(missingMandatoryTouchpoints).length ||
              !isDdxPickedForAllCountries(storedCountryForms)
            }
            onClick={handleClickSavePlan}
            variant="contained"
            sx={{ borderRadius: '30px', height: '34px', minWidth: '153px' }}
          >
            {getButtonContent()}
          </Button>
        </Grid>
      </Grid>

      <SavedPlanSnackbar
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
      />

      <SaveBookingFlow
        open={saveWithBookMetaOpen}
        onClose={handleCloseBookMeta}
        onSuccess={handleSavePlanOnly}
        onSecondaryAction={handleClickBookMeta}
        header=""
      />
      <AcceptPlanFlow
        open={acceptPlanOpen}
        onClose={handleCloseAcceptPlan}
        onSuccess={handleAcceptPlan}
        status={statuses.BOOKED}
        error={acceptError}
        isLoading={isAcceptingMediaPlan}
      />
      <BookingFlow
        open={bookMetaOpen}
        onClose={handleCloseBookMeta}
        onSuccess={() => setBookMetaOpen(false)}
        header=""
        isLoading={
          isBookingLoading ||
          isPolling ||
          pollingResults?.data.jobStatus === 'processing' ||
          pollingResults?.data.jobStatus === 'initiated'
        }
        error={bookingError}
        successData={pollingResults?.data.results}
      />
    </Box>
  );
}

export default PlanSummary;
